import { Cart, CartItem } from "@/types";

export type OperationType = "ADD" | "UPDATE" | "REMOVE" | "CLEAR";

export type OperationStatus =
  | "PENDING"
  | "IN_FLIGHT"
  | "COMPLETED"
  | "FAILED";

export interface AddPayload {
  tempId: string;
  serverId?: string;
  item: CartItemData;
}

export interface UpdatePayload {
  itemId: string;
  changes: {
    quantity?: number;
    specialInstructions?: string;
  };
}

export interface RemovePayload {
  itemId: string;
}

export type ClearPayload = Record<string, never>;

export type OperationPayload =
  | AddPayload
  | UpdatePayload
  | RemovePayload
  | ClearPayload;

export interface CartOperation {
  id: string;
  type: OperationType;
  timestamp: number;
  idempotencyKey: string;
  payload: OperationPayload;
  status: OperationStatus;
  retryCount: number;
  lastError?: string;
  previousState?: CartItem | Cart;
}

export interface QueueState {
  operations: CartOperation[];
  lastSyncedVersion: number;
  lastSyncTimestamp: number;
}

export interface CartItemData {
  itemOfferId: string;
  categoryId: string;
  quantity: number;
  unitPrice: number;
  itemTotalPrice: number;
  specialInstructions: string;
  options: CartItemOptionData[];
  item: {
    id: string;
    name: string;
    image?: { URL: string; "CRC-32"?: string };
  };
  itemOffer: {
    id: string;
    price: { value: number };
  };
  optionGroups: Array<{
    id: string;
    name: string;
    options: Array<{
      id: string;
      name: string;
    }>;
  }>;
  itemOptions: Record<string, { id: string; name: string; price?: { value: number } }>;
  optionQuantities: Record<string, Record<string, number>>;
}

export interface CartItemOptionData {
  optionGroupId: string;
  optionId: string;
  categoryId?: string;
  quantity: number;
  specialInstructions?: string;
}

export interface SyncEngineConfig {
  debounceMs: number;
  maxRetries: number;
  baseBackoffMs: number;
  maxBackoffMs: number;
  flushOnNavigate: boolean;
}

export type CartErrorType =
  | "NETWORK"
  | "SERVER"
  | "RATE_LIMIT"
  | "VALIDATION"
  | "AUTH"
  | "NOT_FOUND"
  | "CONFLICT"
  | "UNKNOWN";

export interface CartError {
  type: CartErrorType;
  message: string;
  operation?: CartOperation;
  recoverable: boolean;
  userMessage: string;
}

export type CrossTabMessageType =
  | "CART_UPDATED"
  | "CART_CLEARED"
  | "REQUEST_STATE"
  | "STATE_RESPONSE";

export interface CrossTabMessage {
  type: CrossTabMessageType;
  timestamp: number;
  tabId: string;
  payload?: Cart;
}

export interface BatchAddResponse {
  items: Array<{
    tempId: string;
    id: string;
  }>;
}

export function isAddPayload(payload: OperationPayload): payload is AddPayload {
  return "tempId" in payload && "item" in payload;
}

export function isUpdatePayload(
  payload: OperationPayload
): payload is UpdatePayload {
  return "itemId" in payload && "changes" in payload;
}

export function isRemovePayload(
  payload: OperationPayload
): payload is RemovePayload {
  return "itemId" in payload && !("changes" in payload);
}

export function isClearPayload(
  payload: OperationPayload
): payload is ClearPayload {
  return Object.keys(payload).length === 0;
}
